/**
 * Cliente para interactuar con la API de Strapi
 */
import axios from 'axios';
import qs from 'qs';

// URL base de la API de Strapi
export const STRAPI_URL = 'https://cms.jaradpetfood.com';
export const STRAPI_API_URL = `${STRAPI_URL}/api`;

// API Key para autenticar las solicitudes
export const STRAPI_API_KEY = '80cf0c0618031377ec929b1b0db3029b83fe6dcb24b43fa2034898863582ec4b0289112b5f90bcb3b5ad2b8c6fc39ad8624b226696ecedb041b7d6c1869021a103da4e60f9fdf2b4a672ef453b912749558cbf8dab194c334feae863d0ab69d5a691ae7988bcc2387028535b9077c531d0c320778362be48d03fd91cace96493';

// Opciones por defecto para las peticiones
const defaultOptions = {
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${STRAPI_API_KEY}`,
  },
};

/**
 * Realiza una petición GET a la API de Strapi
 * @param endpoint - El endpoint de la API de Strapi (sin /api)
 * @param queryParams - Parámetros de consulta opcionales
 * @returns Los datos de la respuesta
 */
export async function fetchAPI(
  endpoint: string,
  queryParams: Record<string, string | number | boolean> = {}
) {
  try {
    // Construir la URL con los parámetros de consulta usando qs para manejar objetos anidados
    const queryString = qs.stringify(queryParams, { encodeValuesOnly: true });
    const url = `${STRAPI_API_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;
    
    // Depuración: Registrar la URL completa para ayudar a diagnosticar problemas
    console.log(`Strapi API Request: ${url}`);

    // Realizar la petición con axios
    const response = await axios.get(url, defaultOptions);

    // Depuración: Registrar éxito
    console.log(`Strapi API Response OK: ${endpoint}`);

    // Devolver los datos
    return response.data;
  } catch (error) {
    console.error('Error fetching from Strapi:', error);
    throw error;
  }
}

/**
 * Obtiene la URL completa para una imagen de Strapi
 * @param imagePath - La ruta relativa de la imagen o el objeto imagen completo
 * @returns La URL completa de la imagen
 */
export function getStrapiMediaUrl(imageInput: string | null | undefined | any): string {
  // Si no hay imagen, devolver cadena vacía
  if (!imageInput) {
    return '';
  }
  
  // Si es un objeto imagen (formato nuevo o directo)
  if (typeof imageInput === 'object') {
    // Caso 1: La imagen es un objeto directo con url
    if (imageInput.url) {
      const url = imageInput.url;
      
      // Si es una URL completa, devolverla
      if (url.startsWith('http')) {
        return url;
      }
      
      // Si es una ruta relativa, añadir la URL base de Strapi
      return `${STRAPI_URL}${url}`;
    }
    
    // Caso 2: La imagen tiene formato de datos anidados (estructura Strapi standard)
    if (imageInput.data && imageInput.data.attributes) {
      const url = imageInput.data.attributes.url;
      
      // Si es una URL completa, devolverla
      if (url.startsWith('http')) {
        return url;
      }
      
      // Si es una ruta relativa, añadir la URL base de Strapi
      return `${STRAPI_URL}${url}`;
    }
    
    // Caso 3: Si tiene formats y hay versión pequeña, usar esa
    if (imageInput.formats && imageInput.formats.small && imageInput.formats.small.url) {
      const url = imageInput.formats.small.url;
      
      // Si es una URL completa, devolverla
      if (url.startsWith('http')) {
        return url;
      }
      
      // Si es una ruta relativa, añadir la URL base de Strapi
      return `${STRAPI_URL}${url}`;
    }
    
    console.warn('Formato de imagen no reconocido:', imageInput);
    return '';
  }
  
  // Si es una cadena (URL directa)
  // Si es una URL completa, devolverla
  if (imageInput.startsWith('http')) {
    return imageInput;
  }

  // Si es una ruta relativa, añadir la URL base de Strapi
  return `${STRAPI_URL}${imageInput}`;
}

/**
 * Procesa los datos de Strapi para extraer los atributos
 * @param data - Datos de respuesta de Strapi
 * @returns Datos procesados
 */
export function processStrapiData<T>(data: any): T {
  // Si es un array, procesar cada elemento
  if (Array.isArray(data)) {
    return data.map(item => processStrapiItem(item)) as unknown as T;
  }
  
  // Si es un objeto, procesar el objeto
  return processStrapiItem(data) as T;
}

/**
 * Procesa un elemento de Strapi para extraer los atributos
 * @param item - Elemento de Strapi
 * @returns Elemento procesado
 */
function processStrapiItem<T>(item: any): T {
  // Si el elemento tiene un campo 'attributes', extraer los atributos
  if (item && item.attributes) {
    return {
      id: item.id,
      ...item.attributes,
    } as T;
  }
  
  // Si no tiene 'attributes', devolver el elemento como está
  return item as T;
} 